import { useState } from "react";
import moment from "moment";
import {
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import TableWrapper from "components/TableWrapper";
import Loader from "components/Loader";
import useToaster from "hooks/useToaster";
import {
  OFF_DAY_WORKING,
  OFF_DAY_WORKING_OPTIONS,
  OFF_DAYS_COLUMNS,
} from "constants/eddConfig";
import { validateOffDay } from "utils/eddUtils";

const getWorkingValue = (isWorking) =>
  isWorking ? OFF_DAY_WORKING.YES : OFF_DAY_WORKING.NO;

const getWorkingLabel = (isWorking) =>
  OFF_DAY_WORKING_OPTIONS.find((o) => o.value === getWorkingValue(isWorking))
    ?.label;

const OffDaysTable = ({ rows, isLoading, savingId, onSave }) => {
  const triggerToaster = useToaster();
  const [editRow, setEditRow] = useState(null);

  const getRowKey = (row) => row.id ?? row.date;
  const isEditing = (row) =>
    Boolean(editRow) && getRowKey(editRow) === getRowKey(row);

  const handleEditChange = (field, value) => {
    setEditRow((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "is_working" && !value) {
        next.start_time = null;
        next.end_time = null;
      }
      return next;
    });
  };

  const handleSave = async () => {
    const error = validateOffDay(editRow);
    if (error) {
      triggerToaster(error, "error");
      return;
    }
    const isSuccess = await onSave(editRow);
    if (isSuccess) setEditRow(null);
  };

  const tableHeader = OFF_DAYS_COLUMNS.map((col) => (
    <TableCell
      key={col.field}
      sx={{ whiteSpace: "nowrap", minWidth: col.minWidth }}
      align={col.field === "action" ? "right" : "left"}
    >
      {col.headerName}
    </TableCell>
  ));

  const renderTimeInput = (field) => (
    <TextField
      type="time"
      size="small"
      value={editRow[field] || ""}
      disabled={!editRow.is_working}
      onChange={(e) => handleEditChange(field, e.target.value)}
      inputProps={{ "aria-label": field, step: 300 }}
      sx={{ width: 130 }}
    />
  );

  const renderEditCells = () => (
    <>
      <TableCell>
        <TextField
          select
          size="small"
          value={getWorkingValue(editRow.is_working)}
          onChange={(e) =>
            handleEditChange(
              "is_working",
              e.target.value === OFF_DAY_WORKING.YES,
            )
          }
          sx={{ width: 190 }}
        >
          {OFF_DAY_WORKING_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell>{renderTimeInput("start_time")}</TableCell>
      <TableCell>{renderTimeInput("end_time")}</TableCell>
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={editRow.reason || ""}
          onChange={(e) => handleEditChange("reason", e.target.value)}
          inputProps={{ "aria-label": "reason", maxLength: 200 }}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          size="small"
          checked={Boolean(editRow.is_active)}
          onChange={(e) => handleEditChange("is_active", e.target.checked)}
          inputProps={{ "aria-label": "active" }}
        />
      </TableCell>
    </>
  );

  const renderViewCells = (row) => (
    <>
      <TableCell>{getWorkingLabel(row.is_working)}</TableCell>
      <TableCell>{row.start_time || "—"}</TableCell>
      <TableCell>{row.end_time || "—"}</TableCell>
      <TableCell>{row.reason || "-"}</TableCell>
      <TableCell>
        <Checkbox
          size="small"
          checked={Boolean(row.is_active)}
          disabled
          inputProps={{ "aria-label": "active" }}
        />
      </TableCell>
    </>
  );

  const renderActions = (row) => {
    const isRowSaving = savingId === getRowKey(row);
    if (isEditing(row)) {
      return (
        <Box display="flex" justifyContent="flex-end">
          <Tooltip title="Save">
            <span>
              <IconButton
                size="small"
                color="primary"
                aria-label="save off day"
                disabled={isRowSaving}
                onClick={handleSave}
              >
                {isRowSaving ? (
                  <Loader size={16} sx={{ marginTop: 0 }} />
                ) : (
                  <CheckIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton
                size="small"
                aria-label="cancel edit"
                disabled={isRowSaving}
                onClick={() => setEditRow(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    }
    return (
      <Tooltip title="Edit">
        <span>
          <IconButton
            size="small"
            aria-label="edit off day"
            disabled={Boolean(editRow) || savingId !== null}
            onClick={() => setEditRow({ ...row })}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderMessageRow = (key, content) => [
    <TableRow key={key}>
      <TableCell
        colSpan={OFF_DAYS_COLUMNS.length}
        align="center"
        sx={{ py: 4 }}
      >
        {content}
      </TableCell>
    </TableRow>,
  ];

  const tableBody = isLoading
    ? renderMessageRow("loading", <Loader size={28} sx={{ marginTop: 0 }} />)
    : !rows.length
    ? renderMessageRow(
        "empty",
        <Typography color="textSecondary">No off days added</Typography>,
      )
    : rows.map((row) => (
        <TableRow key={getRowKey(row)} hover>
          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            {moment(row.date).format("DD MMM YYYY")}
          </TableCell>
          <TableCell>{moment(row.date).format("dddd")}</TableCell>
          {isEditing(row) ? renderEditCells() : renderViewCells(row)}
          <TableCell align="right">{renderActions(row)}</TableCell>
        </TableRow>
      ));

  return <TableWrapper tableHeader={tableHeader} tableBody={tableBody} />;
};

OffDaysTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  savingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSave: PropTypes.func.isRequired,
};

OffDaysTable.defaultProps = {
  savingId: null,
};

export default OffDaysTable;
